import React, { useState, useEffect, useCallback } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useApp } from '../contexts/AppContext';
import { Skill } from '../types';
import { ActionBar } from './ActionBar';
import { SkillPanel } from './SkillPanel';
import { EffectBar } from './EffectBar';
import { JobGauge } from './JobGauge';
import { GitHubLink } from './GitHubLink';
import { CharacterDisplay } from './CharacterDisplay';
import { useSkillCooldowns } from '../hooks/useSkillCooldowns';
import { useSkillEffects } from '../hooks/useSkillEffects';
import { useComboSystem } from '../hooks/useComboSystem';
import { useSkillHighlight } from '../hooks/useSkillHighlight';
import { loadGlobalConfig } from '../services/globalConfig';
import { useCharacterState } from '../hooks/useCharacterState';
import { GAME_CONSTANTS } from '../constants';

interface PracticeAreaProps {
  onBackToJobSelection: () => void;
}

export const PracticeArea: React.FC<PracticeAreaProps> = ({ onBackToJobSelection }) => {
  const { state, toggleKeybindMode, toggleActionBarLock, setGcd } = useApp();
  const currentGcd = state.currentJob?.gcd || GAME_CONSTANTS.DEFAULT_GCD;
  const { 
    activeEffects, 
    processSkillEffects, 
    updateEffectTimers, 
    clearAllEffects,
    removeEffect,
    consumeEffectStacks
  } = useSkillEffects();
  const { startCooldown, isSkillOnCooldown, getCooldownRemaining, getChargeInfo, resetAllCooldowns } = useSkillCooldowns(currentGcd, activeEffects);
  const {
    comboState,
    useSkill: processComboSkill,
    resetCombo,
    isComboExpired
  } = useComboSystem();
  const {
    isSkillUsable,
    getHighlightType,
    getHighlightStyle,
    createGameState
  } = useSkillHighlight();
  const [combatTime, setCombatTime] = useState(0);
  const [isInCombat, setIsInCombat] = useState(false);
  const [lastCombatAction, setLastCombatAction] = useState(0);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [tempGcd, setTempGcd] = useState(state.currentJob?.gcd || GAME_CONSTANTS.DEFAULT_GCD);
  const [globalConfig, setGlobalConfig] = useState<any>(null);
  const { isActive: isCharacterActive, activate: activateCharacter } = useCharacterState();
  const { isActive: isDummyActive, activate: activateDummy } = useCharacterState();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isInCombat) {
      interval = setInterval(() => {
        setCombatTime(prev => prev + 0.1);
        updateEffectTimers(); // 更新效果计时器
        
        // 检查连击是否过期
        if (isComboExpired(Date.now())) {
          resetCombo();
        }
        
        // 检查是否应该退出战斗状态（15秒内没有攻击行为）
        const now = Date.now();
        if (lastCombatAction > 0 && now - lastCombatAction > GAME_CONSTANTS.COMBAT_TIMEOUT) {
          setIsInCombat(false);
          setCombatTime(0);
          setLastCombatAction(0);
          console.log('战斗状态结束');
        }
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isInCombat, lastCombatAction]); // eslint-disable-line react-hooks/exhaustive-deps

  // 初始化全局配置
  useEffect(() => {
    loadGlobalConfig().then(config => {
      setGlobalConfig(config);
    });
  }, []);


  // 监听职业变化，初始化tempGcd
  useEffect(() => {
    if (state.currentJob) {
      const currentGcd = state.currentJob.gcd || GAME_CONSTANTS.DEFAULT_GCD;
      setTempGcd(currentGcd);
    }
  }, [state.currentJob]);

  // 监听职业GCD变化
  useEffect(() => {
    if (state.currentJob?.gcd !== undefined) {
      // 如果配置面板没有打开，同步更新tempGcd
      if (!showConfigModal) {
        setTempGcd(state.currentJob.gcd);
      }
    }
  }, [state.currentJob?.gcd, showConfigModal]);

  const handleSkillUse = useCallback((skill: Skill) => {
    if (isSkillOnCooldown(skill)) {
      console.log(`技能 ${skill.name} 仍在冷却中`);
      return;
    }

    // 检查技能是否可用（包括资源和状态检查）
    const gameState = createGameState(activeEffects, comboState, state.currentJob, isInCombat);
    if (!isSkillUsable(skill, gameState)) {
      console.log(`技能 ${skill.name} 无法使用 - 条件不满足`);
      return;
    }

    if (!isInCombat) {
      setIsInCombat(true);
    }
    
    // 更新最后一次战斗行为时间
    setLastCombatAction(Date.now());
    
    // 处理连击系统
    const comboResult = processComboSkill(skill, state.currentJob?.skills || []);
    
    // 处理技能效果
    processSkillEffects(skill);
    
    // 处理资源消耗（完全基于配置）
    if (skill.costs && skill.costs.length > 0) {
      skill.costs.forEach(cost => {
        if (cost.type === 'status') {
          // 消耗状态效果
          removeEffect(cost.resource);
          console.log(`消耗了状态: ${cost.resource}`);
        } else if (cost.type === 'gauge') {
          // 处理量谱类型的消耗
          console.log(`消耗了量谱: ${cost.resource} ${cost.value}点`);
        } else if (cost.type === 'mp') {
          // 处理MP消耗
          console.log(`消耗了MP: ${cost.value}点`);
        } else if (cost.type === 'tp') {
          // 处理TP消耗
          console.log(`消耗了TP: ${cost.value}点`);
        }
      });
    }
    
    // 启动冷却时间，并传入消耗效果层数的回调
    startCooldown(skill, (effectId: string, consumeAmount: number) => {
      consumeEffectStacks(effectId, consumeAmount);
    });
    
    // 激活角色和假人状态
    activateCharacter();
    activateDummy();
    
    console.log(`使用技能: ${skill.name}${comboResult.wasCombo ? ' (连击!)' : ''}`);
  }, [isInCombat, isSkillOnCooldown, startCooldown, createGameState, activeEffects, comboState, state.currentJob, isSkillUsable, processSkillEffects, processComboSkill, removeEffect, consumeEffectStacks, activateCharacter, activateDummy]);

  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (state.isKeybindMode) return;
      
      const key = event.key.toUpperCase();
      
      for (let barIndex = 0; barIndex < state.actionBars.length; barIndex++) {
        const actionBar = state.actionBars[barIndex];
        const slotIndex = actionBar.keyBindings[key];
        
        if (slotIndex !== undefined && actionBar.slots[slotIndex]) {
          event.preventDefault();
          handleSkillUse(actionBar.slots[slotIndex]!);
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [state.actionBars, state.isKeybindMode, handleSkillUse]);

  const resetCombat = () => {
    setIsInCombat(false);
    setCombatTime(0);
    setLastCombatAction(0);
    clearAllEffects();
    resetCombo();
    resetAllCooldowns();
    console.log('已重置所有状态');
  };

  const openConfigModal = () => {
    setTempGcd(state.currentJob?.gcd || 2.5);
    setShowConfigModal(true);
  };

  const closeConfigModal = () => {
    setShowConfigModal(false);
  };

  const applyConfigChanges = () => {
    // 校验GCD值范围（1到5秒）
    if (tempGcd < 1.0 || tempGcd > 5.0 || isNaN(tempGcd)) {
      alert('GCD时间必须在1.0到5.0秒之间！');
      return;
    }
    
    setGcd(tempGcd);
    setShowConfigModal(false);
  };

  if (!state.currentJob) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'white',
        backgroundColor: '#1a1a1a'
      }}>
        请先选择一个职业
      </div>
    );
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div style={{
        backgroundColor: '#1a1a1a',
        color: 'white',
        minHeight: '100vh',
        padding: '16px'
      }}>
        {/* 顶部信息栏 */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px',
          backgroundColor: '#2a2a2a',
          borderRadius: '8px',
          border: '1px solid #444'
        }}>
          <div style={{ display: 'flex', gap: '20px' }}>
            <span>职业: {state.currentJob.name}</span>
            <span>战斗时间: {combatTime.toFixed(1)}s</span>
            <span>GCD: {state.currentJob.gcd || 2.5}s</span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={onBackToJobSelection}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              返回职业选择
            </button>
            <button
              onClick={toggleKeybindMode}
              style={{
                padding: '8px 16px',
                backgroundColor: state.isKeybindMode ? '#E24A4A' : '#4A90E2',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              {state.isKeybindMode ? '退出键位模式' : '键位绑定模式'}
            </button>
            <button
              onClick={resetCombat}
              style={{
                padding: '8px 16px',
                backgroundColor: '#666',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              重置
            </button>
            <button
              onClick={openConfigModal}
              style={{
                padding: '8px 16px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              配置
            </button>
            <GitHubLink />
          </div>
        </div>

        {/* 主要区域 - 新布局 */}
        <div style={{ display: 'flex', gap: '16px', height: 'calc(100vh - 120px)' }}>
          {/* 左侧技能面板 */}
          <div style={{ flex: '0 0 300px', height: '100%' }}>
            <SkillPanel />
          </div>

          {/* 右侧区域 - 分为上下两部分 */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', height: '100%' }}>
            {/* 上方角色和训练假人区域 (1/3高度) */}
            <div style={{
              flex: '0 0 33.33%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#2a2a2a',
              borderRadius: '8px',
              border: '1px solid #444',
              position: 'relative',
              gap: '80px'
            }}>
              {/* 角色显示 - 左侧 */}
              {state.currentJob?.appearance && globalConfig && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <CharacterDisplay
                    appearance={state.currentJob.appearance}
                    isActive={isCharacterActive}
                    width={80}
                    height={120}
                  />
                  <div style={{ color: '#ccc', fontSize: '12px' }}>角色</div>
                </div>
              )}

              {/* 训练假人显示 - 右侧 */}
              {globalConfig && (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <CharacterDisplay
                    appearance={state.currentJob?.appearance || globalConfig.trainingDummy.appearance}
                    isActive={isDummyActive}
                    width={80}
                    height={120}
                  />
                  <div style={{ color: '#ccc', fontSize: '12px' }}>训练假人</div>
                </div>
              )}
              
              {isInCombat && (
                <div style={{
                  position: 'absolute',
                  top: '16px',
                  right: '16px',
                  background: 'rgba(255, 107, 107, 0.1)',
                  border: '1px solid #ff6b6b',
                  borderRadius: '4px',
                  padding: '8px 12px',
                  color: '#ff6b6b',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}>
                  <div>战斗中</div>
                  <div style={{ fontSize: '12px', opacity: 0.8 }}>
                    时长: {combatTime.toFixed(1)}s
                  </div>
                  {lastCombatAction > 0 && (
                    <div style={{ fontSize: '10px', opacity: 0.6 }}>
                      脱战倒计时: {Math.max(0, 15 - (Date.now() - lastCombatAction) / 1000).toFixed(1)}s
                    </div>
                  )}
                </div>
              )}

              {/* 状态效果栏 - 放在假人区域内 */}
              <div style={{
                position: 'absolute',
                bottom: '16px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '90%'
              }}>
                <EffectBar effects={activeEffects} />
              </div>
              
              {/* 量谱显示 - 放在训练假人区域的右下角 */}
              <div style={{
                position: 'absolute',
                bottom: '16px',
                right: '16px'
              }}>
                <JobGauge job={state.currentJob} activeEffects={activeEffects} />
              </div>
            </div>

            {/* 下方操作栏区域 (2/3高度) */}
            <div style={{
              flex: '0 0 66.67%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '16px',
              backgroundColor: '#2a2a2a',
              borderRadius: '8px',
              border: '1px solid #444',
              padding: '20px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px', 
                marginBottom: '8px' 
              }}>
                <div style={{ fontSize: '14px', color: '#ccc' }}>
                  操作栏 - 拖拽技能到操作栏进行配置
                </div>
                <button
                  onClick={toggleActionBarLock}
                  style={{
                    padding: '6px',
                    backgroundColor: 'transparent',
                    color: state.isActionBarLocked ? '#28a745' : '#dc3545',
                    border: `1px solid ${state.isActionBarLocked ? '#28a745' : '#dc3545'}`,
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    minWidth: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={state.isActionBarLocked ? '操作栏已锁定 - 点击解锁' : '操作栏已解锁 - 点击锁定'}
                >
                  {state.isActionBarLocked ? '🔒' : '🔓'}
                </button>
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
                alignItems: 'center'
              }}>
                {[0, 1, 2].map(barIndex => {
                  const gameState = createGameState(activeEffects, comboState, state.currentJob, isInCombat);
                  return (
                    <ActionBar 
                      key={barIndex} 
                      barIndex={barIndex} 
                      onSkillUse={handleSkillUse}
                      isSkillOnCooldown={isSkillOnCooldown}
                      getCooldownRemaining={getCooldownRemaining}
                      getHighlightType={(skill) => getHighlightType(skill, gameState)}
                      getHighlightStyle={getHighlightStyle}
                      isSkillUsable={(skill) => isSkillUsable(skill, gameState)}
                      getChargeInfo={getChargeInfo}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {state.isKeybindMode && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: '2px solid #E24A4A'
          }}>
            键位绑定模式已激活 - 点击技能槽位后按下键盘按键进行绑定
          </div>
        )}

        {/* 配置模态框 */}
        {showConfigModal && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}>
            <div style={{
              backgroundColor: '#2a2a2a',
              borderRadius: '8px',
              border: '1px solid #444',
              padding: '24px',
              minWidth: '400px',
              color: 'white'
            }}>
              <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#f0f0f0' }}>职业配置</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px' }}>
                  GCD时间 (秒)
                </label>
                <input
                  type="number"
                  min="1.0"
                  max="5.0"
                  step="0.1"
                  value={tempGcd || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === '') {
                      setTempGcd(0); // 允许清空
                    } else {
                      const numValue = parseFloat(value);
                      setTempGcd(numValue);
                    }
                  }}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    backgroundColor: '#3a3a3a',
                    color: 'white',
                    border: '1px solid #555',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                  允许范围: 1.0 - 5.0 秒
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={closeConfigModal}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  取消
                </button>
                <button
                  onClick={applyConfigChanges}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  应用
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </DndProvider>
  );
};
